import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Building, 
  MapPin, 
  Phone, 
  Mail, 
  Users, 
  FolderOpen, 
  Edit, 
  Trash2,
  Plus,
  Calendar,
  ToggleLeft,
  ToggleRight,
  Eye
} from 'lucide-react';
import { clientDetailData } from '../../data/mockData';

// Données importées depuis mockData
const clientDetails = clientDetailData;

const ClientDetail = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('informations');
  const [isEditing, setIsEditing] = useState(false);
  
  const client = clientDetailData[id];

  if (!client) {
    return (
      <div className="text-center py-12">
        <Building className="h-12 w-12 text-port-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-port-gray-900 mb-2">Client non trouvé</h3>
        <Link to="/admin/clients" className="btn btn-primary">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour aux clients
        </Link>
      </div>
    );
  }

  const toggleStatus = () => {
    // Ici on pourrait faire l'appel API pour changer le statut
    console.log('Toggle status for client', id);
  };

  return (
    <div className="animate-slide-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Link 
            to="/admin/clients" 
            className="p-2 text-port-gray-600 hover:bg-port-gray-100 rounded-lg transition-colors mr-4"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-port-gray-900">{client.raisonSociale}</h1>
            <p className="text-port-gray-600 flex items-center mt-1">
              <Building className="h-4 w-4 mr-1" />
              {client.secteur}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Statut Toggle */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-port-gray-600">Statut:</span>
            <button
              onClick={toggleStatus}
              className={`flex items-center space-x-1 ${
                client.statut === 'actif' ? 'text-atl-green-600' : 'text-port-gray-400'
              }`}
            >
              {client.statut === 'actif' ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
              <span className="text-sm font-medium">
                {client.statut === 'actif' ? 'Actif' : 'Inactif'}
              </span>
            </button>
          </div>
          
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="btn btn-secondary"
          >
            <Edit className="h-4 w-4 mr-2" />
            {isEditing ? 'Annuler' : 'Modifier'}
          </button>
        </div>
      </div>

      {/* Onglets */}
      <div className="border-b border-port-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('informations')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'informations'
                ? 'border-atl-blue-500 text-atl-blue-600'
                : 'border-transparent text-port-gray-500 hover:text-port-gray-700 hover:border-port-gray-300'
            }`}
          >
            Informations
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'contacts'
                ? 'border-atl-blue-500 text-atl-blue-600'
                : 'border-transparent text-port-gray-500 hover:text-port-gray-700 hover:border-port-gray-300'
            }`}
          >
            Contacts ({client.contacts.length})
          </button>
          <button
            onClick={() => setActiveTab('dossiers')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'dossiers'
                ? 'border-atl-blue-500 text-atl-blue-600'
                : 'border-transparent text-port-gray-500 hover:text-port-gray-700 hover:border-port-gray-300'
            }`}
          >
            Historique Dossiers ({client.historiquesDossiers.length})
          </button>
        </nav>
      </div>

      {/* Contenu des onglets */}
      {activeTab === 'informations' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Informations générales */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium">Informations Générales</h3>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                <div>
                  <label className="form-label">Raison Sociale</label>
                  {isEditing ? (
                    <input type="text" defaultValue={client.raisonSociale} className="form-input" />
                  ) : (
                    <p className="text-port-gray-900">{client.raisonSociale}</p>
                  )}
                </div>
                
                <div>
                  <label className="form-label">Secteur d'activité</label>
                  {isEditing ? (
                    <select defaultValue={client.secteur} className="form-select">
                      <option value="Assurance">Assurance</option>
                      <option value="Mutuelle">Mutuelle</option>
                      <option value="Banque-Assurance">Banque-Assurance</option>
                    </select>
                  ) : (
                    <p className="text-port-gray-900">{client.secteur}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">SIRET</label>
                  {isEditing ? (
                    <input type="text" defaultValue={client.siret} className="form-input" />
                  ) : (
                    <p className="text-port-gray-900">{client.siret}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">Numéro TVA</label>
                  {isEditing ? (
                    <input type="text" defaultValue={client.numeroTVA} className="form-input" />
                  ) : (
                    <p className="text-port-gray-900">{client.numeroTVA}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Coordonnées */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium">Coordonnées</h3>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                <div>
                  <label className="form-label flex items-center">
                    <MapPin className="h-4 w-4 mr-1" />
                    Adresse
                  </label>
                  {isEditing ? (
                    <textarea defaultValue={client.adresse} className="form-textarea" rows="3" />
                  ) : (
                    <p className="text-port-gray-900">{client.adresse}</p>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Code Postal</label>
                    {isEditing ? (
                      <input type="text" defaultValue={client.codePostal} className="form-input" />
                    ) : (
                      <p className="text-port-gray-900">{client.codePostal}</p>
                    )}
                  </div>
                  <div>
                    <label className="form-label">Ville</label>
                    {isEditing ? (
                      <input type="text" defaultValue={client.ville} className="form-input" />
                    ) : (
                      <p className="text-port-gray-900">{client.ville}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="form-label flex items-center">
                    <Phone className="h-4 w-4 mr-1" />
                    Téléphone
                  </label>
                  {isEditing ? (
                    <input type="tel" defaultValue={client.telephone} className="form-input" />
                  ) : (
                    <p className="text-port-gray-900">{client.telephone}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">Fax</label>
                  {isEditing ? (
                    <input type="tel" defaultValue={client.fax} className="form-input" />
                  ) : (
                    <p className="text-port-gray-900">{client.fax}</p>
                  )}
                </div>

                <div>
                  <label className="form-label flex items-center">
                    <Mail className="h-4 w-4 mr-1" />
                    Email
                  </label>
                  {isEditing ? (
                    <input type="email" defaultValue={client.email} className="form-input" />
                  ) : (
                    <p className="text-port-gray-900">{client.email}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">Site Web</label>
                  {isEditing ? (
                    <input type="url" defaultValue={client.siteWeb} className="form-input" />
                  ) : (
                    <p className="text-port-gray-900">
                      <a href={`https://${client.siteWeb}`} target="_blank" rel="noopener noreferrer" className="text-atl-blue-600 hover:underline">
                        {client.siteWeb}
                      </a>
                    </p>
                  )}
                </div>
              </div>

              {isEditing && (
                <div className="mt-6 pt-4 border-t border-port-gray-200">
                  <div className="flex space-x-3">
                    <button className="btn btn-primary">
                      Enregistrer
                    </button>
                    <button 
                      onClick={() => setIsEditing(false)}
                      className="btn btn-secondary"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Informations système */}
          <div className="card lg:col-span-2">
            <div className="card-header">
              <h3 className="text-lg font-medium">Informations Système</h3>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="form-label flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    Date de création
                  </label>
                  <p className="text-port-gray-900">{new Date(client.dateCreation).toLocaleDateString('fr-FR')}</p>
                </div>
                <div>
                  <label className="form-label">Dernière modification</label>
                  <p className="text-port-gray-900">{new Date(client.derniereModification).toLocaleDateString('fr-FR')}</p>
                </div>
                <div>
                  <label className="form-label flex items-center">
                    <FolderOpen className="h-4 w-4 mr-1" />
                    Nombre de dossiers
                  </label>
                  <p className="text-2xl font-bold text-atl-blue-600">{client.nombreDossiers}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'contacts' && (
        <div className="space-y-6">
          {/* Header avec bouton d'ajout */}
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Contacts rattachés</h3>
            <button className="btn btn-primary btn-sm">
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un contact
            </button>
          </div>

          {/* Liste des contacts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {client.contacts.map(contact => (
              <div key={contact.id} className="card">
                <div className="card-body">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-atl-blue-100 rounded-full flex items-center justify-center mr-3">
                        <Users className="h-5 w-5 text-atl-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-port-gray-900">{contact.nom}</h4>
                        <p className="text-sm text-port-gray-500">{contact.poste}</p>
                      </div>
                    </div>
                    <div className="flex space-x-1">
                      <button className="p-1 text-port-gray-400 hover:text-port-gray-600">
                        <Edit className="h-4 w-4" />
                      </button>
                      <button className="p-1 text-red-400 hover:text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 text-port-gray-400 mr-2" />
                      <a href={`mailto:${contact.email}`} className="text-atl-blue-600 hover:underline">
                        {contact.email}
                      </a>
                    </div>
                    <div className="flex items-center">
                      <Phone className="h-4 w-4 text-port-gray-400 mr-2" />
                      <span className="text-port-gray-900">{contact.telephone}</span>
                    </div>
                    {contact.mobile && (
                      <div className="flex items-center">
                        <Phone className="h-4 w-4 text-port-gray-400 mr-2" />
                        <span className="text-port-gray-900">{contact.mobile} (mobile)</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'dossiers' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Historique des dossiers</h3>
            <Link to="/admin/dossiers/nouveau" className="btn btn-primary btn-sm">
              <Plus className="h-4 w-4 mr-2" />
              Nouveau dossier
            </Link>
          </div>

          <div className="card">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Référence</th>
                    <th>Type</th>
                    <th>Date ouverture</th>
                    <th>Enquêteur</th>
                    <th>Montant</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {client.historiquesDossiers.map(dossier => (
                    <tr key={dossier.id}>
                      <td className="font-medium">{dossier.reference}</td>
                      <td>{dossier.type}</td>
                      <td>{new Date(dossier.dateOuverture).toLocaleDateString('fr-FR')}</td>
                      <td>{dossier.enqueteur}</td>
                      <td className="font-medium">{dossier.montant}</td>
                      <td>
                        <span className={`badge ${
                          dossier.statut === 'clôturé' ? 'badge-success' :
                          dossier.statut === 'en cours' ? 'badge-warning' :
                          'badge-info'
                        }`}>
                          {dossier.statut}
                        </span>
                      </td>
                      <td>
                        <Link
                          to={`/admin/dossiers/${dossier.id}`}
                          className="p-2 text-atl-blue-600 hover:bg-atl-blue-100 rounded-lg transition-colors"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDetail;
